import React, { useState } from "react";
import { SoundToggle } from "@/components/SoundToggle";
import { playRight, playWrong, playFanfare } from "@/lib/feedbackSounds";
import { Check, X, HelpCircle } from "lucide-react";
import { playSequence } from "@/lib/audio";
import { rotate } from "@/lib/theory";

// items: [{ prompt, seq: ["C","D","?","F"], options: ["E","G"], answer: "E", play?: [pitches] }]
const Item = ({ it, idx, testId, onRight, onWrong }) => {
  const [pick, setPick] = useState(null);
  const right = pick === it.answer;
  const opts = rotate(it.options, it.prompt.length);
  const choose = (o) => { setPick(o); if (o !== it.answer) onWrong(); if (o === it.answer) { onRight(idx); if (it.play) playSequence(it.play, 0.4, 0.4); } };
  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid={`${testId}-${idx}`}>
      <p className="text-sm text-foreground">{it.prompt}</p>
      <div className="mt-2 flex flex-wrap gap-1.5 font-mono">
        {it.seq.map((n, k) => (
          <span key={k} className={`min-w-[2.5rem] rounded-lg px-2 py-1 text-center text-sm ${n === "?" ? (pick ? (right ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-red-500/15 text-red-600") : "border-2 border-dashed border-primary/60 text-primary") : "bg-secondary text-foreground"}`}>
            {n === "?" ? (pick || "?") : n}
          </span>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {opts.map((o) => (
          <button key={o} onClick={() => choose(o)} data-testid={`${testId}-${idx}-opt-${o}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${pick === o ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{o}</button>
        ))}
        {pick && (right
          ? <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid={`${testId}-${idx}-result`}><Check size={13} /> Correct</span>
          : <span className="flex items-center gap-1 text-xs text-red-600" data-testid={`${testId}-${idx}-result`}><X size={13} /> Try again</span>)}
      </div>
    </div>
  );
};

export const MissingNotes = ({ items, testId = "missing", title = "Find the missing notes", onAllCorrect }) => {
  const [ok, setOk] = useState(() => new Set());
  const slips = React.useRef(0);
  const onWrong = () => { slips.current += 1; playWrong(); };
  const onRight = (i) => {
    const s = new Set(ok).add(i);
    if (s.size === items.length && ok.size < items.length) {
      if (!slips.current) playFanfare(0.15, `Check yourself · ${title}`); else playRight();
      if (onAllCorrect) onAllCorrect();
    } else if (!ok.has(i)) playRight();
    setOk(s);
  };
  return (
  <div className="mt-5 rounded-2xl border border-primary/25 bg-primary/5 p-5" data-testid={testId}>
    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><HelpCircle size={14} /> {title}<SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto normal-case tracking-normal" /></p>
    <div className="mt-3 space-y-3">{items.map((it, i) => <Item key={i} it={it} idx={i} testId={testId} onRight={onRight} onWrong={onWrong} />)}</div>
    {onAllCorrect && ok.size === items.length && <p className="mt-3 text-xs font-medium text-emerald-600" data-testid={`${testId}-all-correct`}>All correct — theory check passed!</p>}
  </div>
  );
};
